import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import BetterflyLogo from '../../../../../assets/icons/betterfly-logo.svg?url';
import CustomButton from '../../../../../shared/components/Buttons/CustomButton/CustomButton';
import CustomInput from '../../../../../shared/components/Inputs/CustomInput/CustomInput';
import { CLICK_ACTION, ENTER_EMAIL, MANUAL_METHOD } from '../../../../../shared/constants/tagManager';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_AUTH } from '../../../../../shared/translations/auth';
import { GenericAsyncVoidFunction } from '../../../../../shared/types/genericTypes';
import { validateEmail } from '../../../../../shared/utils/validation/validation';
import './LoginCard.scss';

interface LoginCardProps {
  changeEmail: GenericAsyncVoidFunction;
}

const LoginCard = ({ changeEmail }: LoginCardProps) => {
  const [userEmail, setUserEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { t } = useTranslation();

  const handleChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserEmail(e.target.value);
    setEmailError('');
  };

  const handleNextStep = async () => {
    if (isSubmitting) return;

    const validEmail = validateEmail(userEmail);
    if (!validEmail) {
      setEmailError(t(TRANSLATIONS_AUTH.loginCard.emailError));
      return;
    }

    setIsSubmitting(true);
    try {
      TagManagerService.pushEvent(CLICK_ACTION, ENTER_EMAIL, MANUAL_METHOD);
      await changeEmail(userEmail);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNextStep();
    }
  };

  return (
    <div className="flex flex-col items-center">
      <img src={BetterflyLogo} className="login-card__logo" alt="Betterfly icon" />

      <div className="login-card__inner">
        <div className="login-card__text-container flex flex-col text-center">
          <div className="login-card__title">{t(TRANSLATIONS_AUTH.loginCard.title)}</div>
        </div>

        <div className="login-card__input-section">
          <CustomInput
            id="login-email"
            name="login-email"
            type="email"
            autoComplete="email"
            placeholder={t(TRANSLATIONS_AUTH.loginCard.accessWithCredentials)}
            value={userEmail}
            onChange={handleChangeEmail}
            onKeyDown={handleKeyDown}
            disabled={isSubmitting}
            error={emailError}
          />
        </div>
        <div className="flex justify-center">
          <CustomButton
            variant="primary"
            size="l"
            onClick={handleNextStep}
            loading={isSubmitting}
            disabled={isSubmitting || !userEmail}
            className="w-full"
          >
            {t(TRANSLATIONS_AUTH.loginCard.continue)}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default LoginCard;
